<template>
  <v-container class="my-5">
    <!-- Header -->
    <v-card-title class="px-0 mb-4">
      <v-btn icon @click="$router.go(-1)" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-icon large color="primary" class="mr-2">mdi-dumbbell</v-icon>
      <div>
        <div class="headline font-weight-bold">Mis Áreas</div>
        <div class="caption grey--text">Áreas asignadas como profesor</div>
      </div>
    </v-card-title>

    <!-- Loading -->
    <v-row v-if="loading" justify="center" class="py-10">
      <v-progress-circular indeterminate color="primary" size="52"></v-progress-circular>
    </v-row>

    <!-- Empty state -->
    <v-row v-else-if="myAreas.length === 0" justify="center" class="py-10">
      <v-flex class="text-center grey--text">
        <v-icon large color="grey lighten-2">mdi-domain</v-icon>
        <h3 class="mt-2">No hay áreas asignadas</h3>
        <p class="caption">Contacta al administrador para que te asigne un área</p>
      </v-flex>
    </v-row>

    <!-- Cards grid -->
    <v-row v-else class="mt-1">
      <v-col v-for="area in myAreas" :key="area.id" cols="12" sm="6" md="4" lg="3">
        <v-card
          class="area-card h-100"
          style="border-radius: 16px; overflow: hidden; transition: box-shadow 0.25s ease, transform 0.25s ease; cursor: pointer;"
          elevation="2"
          @click="navigateToCheckin(area.idArea)"
        >
          <!-- Color stripe top -->
          <div :style="`height: 8px; background: ${areaColors[area.idArea] || '#BDBDBD'};`"></div>

          <v-card-text class="pt-4 pb-2">
            <div class="d-flex align-center mb-3">
              <!-- Color avatar -->
              <v-avatar :color="areaColors[area.idArea] || '#BDBDBD'" size="48" class="mr-3 elevation-1">
                <v-icon dark size="24">mdi-map-marker-radius</v-icon>
              </v-avatar>
              <div class="flex-grow-1">
                <div class="subtitle-1 font-weight-bold grey--text text--darken-3 mb-1" style="line-height: 1.2;">
                  {{ area.areaName || 'Área sin nombre' }}
                </div>
                <div class="caption grey--text">{{ area.position }}</div>
              </div>
            </div>

            <!-- Occupancy indicator -->
            <div v-if="occupancyMap[area.idArea]">
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="caption grey--text">Ocupación</span>
                <v-chip
                  :color="getStatusColor(occupancyMap[area.idArea].status)"
                  x-small
                  dark
                >
                  {{ getOccupancyPercent(occupancyMap[area.idArea]) }}%
                </v-chip>
              </div>
              <v-progress-linear
                :value="getOccupancyPercent(occupancyMap[area.idArea])"
                :color="getStatusColor(occupancyMap[area.idArea].status)"
                height="6"
                rounded
                class="mb-1"
              ></v-progress-linear>
              <div class="caption grey--text d-flex justify-space-between">
                <span>{{ occupancyMap[area.idArea].current_count || 0 }} presentes</span>
                <span>Cap. {{ occupancyMap[area.idArea].max_capacity || '—' }}</span>
              </div>
            </div>
            <div v-else-if="loadingOccupancy[area.idArea]" class="text-center py-1">
              <v-progress-circular indeterminate color="grey" size="16" width="2"></v-progress-circular>
            </div>
            <div v-else class="caption grey--text text--lighten-1">
              Sin datos de ocupación
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <!-- Actions -->
          <v-card-actions class="px-3 py-2" @click.stop>
            <v-btn
              small
              depressed
              color="primary"
              :to="`/sports-area/${area.idArea}/checkin`"
              @click.stop
            >
              <v-icon left x-small>mdi-chart-bar</v-icon>
              Ver ocupación
            </v-btn>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  x-small
                  color="primary"
                  @click.stop="refresh(area.idArea)"
                >
                  <v-icon x-small>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Actualizar ocupación</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { mapGetters, mapState } from 'vuex'

const API_BASE_URL = 'https://clubtest.intrare.app'

export default {
  name: 'teacher-my-areas',
  computed: {
    ...mapGetters(['getProfile']),
    ...mapState(['language'])
  },
  data: () => ({
    myAreas: [],
    occupancyMap: {},
    loadingOccupancy: {},
    loading: false,
    areaColors: {}
  }),
  created () {
    this.loadMyAreas()
  },
  methods: {
    apiCall (method, endpoint, data = null) {
      const config = {
        method,
        url: `${API_BASE_URL}${endpoint}`,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      if (data) config.data = data
      return axios(config)
    },
    async loadMyAreas () {
      this.loading = true
      try {
        const res = await this.apiCall('get', '/staff/my-areas/')

        // Handle single area or array
        if (Array.isArray(res.data)) {
          this.myAreas = res.data
        } else {
          this.myAreas = [res.data]
        }

        // Load occupancy for each area
        this.myAreas.forEach(area => {
          this.loadAreaOccupancy(area.idArea)
        })

        // Load area details to get colors
        this.loadAreaDetails()
      } catch (e) {
        console.error('Error loading my areas:', e)
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', 'Error al cargar tus áreas')
      } finally {
        this.loading = false
      }
    },
    async loadAreaDetails () {
      try {
        const res = await this.apiCall('get', '/area/')
        const areas = res.data || []

        areas.forEach(area => {
          this.$set(this.areaColors, area.id, area.color || '#BDBDBD')
        })
      } catch (e) {
        console.error('Error loading area details:', e)
      }
    },
    async loadAreaOccupancy (areaId) {
      this.$set(this.loadingOccupancy, areaId, true)
      try {
        const res = await this.apiCall('get', `/area/${areaId}/occupancy/`)
        this.$set(this.occupancyMap, areaId, res.data)
      } catch (e) {
        console.error('Error loading occupancy for area:', areaId, e)
      } finally {
        this.$set(this.loadingOccupancy, areaId, false)
      }
    },
    getOccupancyPercent (occ) {
      if (!occ || !occ.max_capacity) return 0
      return Math.round((occ.occupancy_level || 0) * 100)
    },
    getStatusColor (status) {
      switch (status) {
        case 'low': return 'green'
        case 'medium': return 'orange'
        case 'high': return 'red'
        default: return 'grey'
      }
    },
    navigateToCheckin (areaId) {
      this.$router.push(`/sports-area/${areaId}/checkin`)
    },
    async refresh (areaId) {
      await this.loadAreaOccupancy(areaId)
      this.$store.commit('snackbar_activate_true')
      this.$store.commit('snackbar_change_message', 'Ocupación actualizada')
    }
  }
}
</script>

<style scoped>
.area-card:hover {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12) !important;
  transform: translateY(-3px);
}

.h-100 {
  height: 100%;
}
</style>
